import React, { useState, useEffect } from 'react';
import { supabase } from './supabaseClient';
import { 
  Package, 
  CheckCircle, 
  AlertCircle, 
  Truck, 
  RefreshCw,
  Search,
  Box,
  Home,
  Clock,
  MapPin
} from 'lucide-react';

/**
 * Box Receiving Dashboard
 * 
 * System to receive boxes back from drivers and return them to circulation
 * - Shows boxes currently with drivers (status: in_use, current_zone: VEHICLE or any zone)
 * - Allows bulk or individual box receiving
 * - Updates box status to 'available' and clears current_zone
 * - Records removal timestamp in box_allocations
 */
const BoxReceivingDashboard = () => {
  const [boxesInField, setBoxesInField] = useState([]);
  const [selectedBoxes, setSelectedBoxes] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterZone, setFilterZone] = useState('all');
  const [successMessage, setSuccessMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    loadBoxesInField();
  }, []);

  const loadBoxesInField = async () => {
    setLoading(true);
    try {
      const { data: boxesData, error: boxesError } = await supabase
        .from('delivery_boxes')
        .select(`
          id,
          box_number,
          status,
          current_zone,
          last_used_at,
          box_allocations (
            id,
            order_id,
            allocated_at,
            removed_at,
            orders!box_allocations_order_id_fkey (
              id,
              order_number,
              status,
              total
            )
          )
        `)
        .eq('status', 'in_use')
        .order('box_number', { ascending: true });

      if (boxesError) throw boxesError;

      // Process boxes to get active allocation info
      const processedBoxes = boxesData.map(box => {
        const activeAllocation = box.box_allocations?.find(
          alloc => alloc.removed_at === null
        );

        return {
          ...box,
          activeAllocation,
          order: activeAllocation?.orders || null
        };
      });

      setBoxesInField(processedBoxes);
    } catch (err) {
      console.error('Error loading boxes:', err);
      setError('Failed to load boxes in field');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleBox = (boxId) => {
    const newSelected = new Set(selectedBoxes);
    if (newSelected.has(boxId)) {
      newSelected.delete(boxId);
    } else {
      newSelected.add(boxId);
    }
    setSelectedBoxes(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedBoxes.size === filteredBoxes.length) {
      setSelectedBoxes(new Set());
    } else {
      setSelectedBoxes(new Set(filteredBoxes.map(box => box.id)));
    }
  };

  const handleReceiveBoxes = async () => {
    if (selectedBoxes.size === 0) {
      setError('Please select at least one box to receive');
      return;
    }

    const confirmMessage = `Are you sure you want to receive ${selectedBoxes.size} box(es) back into circulation?`;
    if (!window.confirm(confirmMessage)) {
      return;
    }

    setProcessing(true);
    setError('');
    setSuccessMessage('');

    try {
      const boxesToReceive = Array.from(selectedBoxes);
      let successCount = 0;
      let failCount = 0;

      for (const boxId of boxesToReceive) {
        try {
          // 1. Find active allocation for this box
          const { data: allocations, error: allocError } = await supabase
            .from('box_allocations')
            .select('id')
            .eq('box_id', boxId)
            .is('removed_at', null)
            .limit(1);

          if (allocError) throw allocError;

          // 2. Mark allocation as removed
          if (allocations && allocations.length > 0) {
            const { error: removeError } = await supabase
              .from('box_allocations')
              .update({ removed_at: new Date().toISOString() })
              .eq('id', allocations[0].id);

            if (removeError) throw removeError;
          }

          // 3. Update box status back to available
          const { error: boxError } = await supabase
            .from('delivery_boxes')
            .update({
              status: 'available',
              current_zone: null,
              last_used_at: new Date().toISOString()
            })
            .eq('id', boxId);

          if (boxError) throw boxError;

          successCount++;
        } catch (err) {
          console.error(`Error receiving box ${boxId}:`, err);
          failCount++;
        }
      }

      // Show results
      if (successCount > 0) {
        setSuccessMessage(
          `✅ Successfully received ${successCount} box(es) back into circulation!`
        );
      }
      if (failCount > 0) {
        setError(`⚠️ Failed to receive ${failCount} box(es)`);
      }

      // Clear selection and reload
      setSelectedBoxes(new Set());
      await loadBoxesInField();

      // Clear success message after 5 seconds
      if (successCount > 0) {
        setTimeout(() => setSuccessMessage(''), 5000);
      }
    } catch (err) {
      console.error('Error receiving boxes:', err);
      setError('Failed to receive boxes: ' + err.message);
    } finally {
      setProcessing(false);
    }
  };

  const getUniqueZones = () => {
    const zones = new Set(boxesInField.map(box => box.current_zone).filter(Boolean));
    return Array.from(zones).sort();
  };

  const filteredBoxes = boxesInField.filter(box => {
    // Search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      if (!box.box_number.toLowerCase().includes(search) &&
          !box.order?.order_number?.toLowerCase().includes(search)) {
        return false;
      }
    }

    // Zone filter
    if (filterZone !== 'all') {
      if (filterZone === 'vehicle' && box.current_zone !== 'VEHICLE') return false;
      if (filterZone !== 'vehicle' && box.current_zone !== filterZone) return false;
    }

    return true;
  });

  const zones = getUniqueZones();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
          <Home size={32} className="text-green-600" />
          Box Receiving Station
        </h1>
        <p className="text-gray-600 mt-2">
          Receive boxes back from drivers and return them to circulation
        </p>
      </div>

      {/* Stats Bar */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl shadow-sm p-6 border-2 border-orange-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Boxes in Field</p>
              <p className="text-3xl font-bold text-orange-600">{boxesInField.length}</p>
            </div>
            <div className="bg-orange-100 p-3 rounded-lg">
              <Truck size={24} className="text-orange-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-6 border-2 border-blue-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Selected</p>
              <p className="text-3xl font-bold text-blue-600">{selectedBoxes.size}</p>
            </div>
            <div className="bg-blue-100 p-3 rounded-lg">
              <CheckCircle size={24} className="text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-6 border-2 border-purple-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Active Zones</p>
              <p className="text-3xl font-bold text-purple-600">{zones.length}</p>
            </div>
            <div className="bg-purple-100 p-3 rounded-lg">
              <MapPin size={24} className="text-purple-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-6 border-2 border-green-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">To Receive</p>
              <p className="text-3xl font-bold text-green-600">{selectedBoxes.size}</p>
            </div>
            <div className="bg-green-100 p-3 rounded-lg">
              <RefreshCw size={24} className="text-green-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Success/Error Messages */}
      {successMessage && (
        <div className="bg-green-50 border-2 border-green-200 rounded-xl p-4 flex items-center gap-3">
          <CheckCircle size={24} className="text-green-600 flex-shrink-0" />
          <p className="text-green-800 font-medium">{successMessage}</p>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4 flex items-center gap-3">
          <AlertCircle size={24} className="text-red-600 flex-shrink-0" />
          <p className="text-red-800 font-medium">{error}</p>
        </div>
      )}

      {/* Filters & Actions */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
          {/* Search */}
          <div className="flex-1 max-w-md">
            <div className="relative">
              <Search size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search box or order number..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
            </div>
          </div>

          {/* Zone Filter */}
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700">Zone:</label>
            <select
              value={filterZone}
              onChange={(e) => setFilterZone(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
            >
              <option value="all">All Zones</option>
              <option value="vehicle">VEHICLE</option>
              {zones.map(zone => (
                <option key={zone} value={zone}>{zone}</option>
              ))}
            </select>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-2">
            <button
              onClick={handleSelectAll}
              disabled={filteredBoxes.length === 0}
              className="px-4 py-2 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              {selectedBoxes.size === filteredBoxes.length ? 'Deselect All' : 'Select All'}
            </button>
            <button
              onClick={handleReceiveBoxes}
              disabled={selectedBoxes.size === 0 || processing}
              className="flex items-center gap-2 px-6 py-2 bg-green-600 text-white font-bold rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {processing ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                  Receiving...
                </>
              ) : (
                <>
                  <RefreshCw size={20} />
                  Receive {selectedBoxes.size > 0 ? `(${selectedBoxes.size})` : 'Boxes'}
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Box List */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-green-600 border-t-transparent"></div>
          </div>
        ) : filteredBoxes.length === 0 ? (
          <div className="text-center py-12">
            <Box size={48} className="text-gray-300 mx-auto mb-4" />
            <p className="text-gray-600 font-medium">
              {boxesInField.length === 0 
                ? '🎉 No boxes in field - All boxes are available!'
                : 'No boxes match your filters'
              }
            </p>
            <p className="text-sm text-gray-500 mt-2">
              {searchTerm || filterZone !== 'all' 
                ? 'Try adjusting your filters'
                : 'Boxes will appear here when they are out for delivery'
              }
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={selectedBoxes.size === filteredBoxes.length && filteredBoxes.length > 0}
                      onChange={handleSelectAll}
                      className="w-5 h-5 text-green-600 border-gray-300 rounded focus:ring-green-500"
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Box
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Order
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Zone
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Last Used
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredBoxes.map((box) => (
                  <tr
                    key={box.id}
                    className={`hover:bg-gray-50 transition-colors ${
                      selectedBoxes.has(box.id) ? 'bg-green-50' : ''
                    }`}
                  >
                    <td className="px-6 py-4">
                      <input
                        type="checkbox"
                        checked={selectedBoxes.has(box.id)}
                        onChange={() => handleToggleBox(box.id)}
                        className="w-5 h-5 text-green-600 border-gray-300 rounded focus:ring-green-500"
                      />
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="bg-orange-100 p-2 rounded-lg">
                          <Package size={20} className="text-orange-600" />
                        </div>
                        <div>
                          <div className="font-bold text-gray-900">Box {box.box_number}</div>
                          <div className="text-xs text-gray-500">{box.id.split('-')[0]}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {box.order ? (
                        <div>
                          <div className="font-medium text-gray-900">#{box.order.order_number}</div>
                          <div className="text-sm text-gray-500">
                            R{box.order.total?.toFixed(2)} • {box.order.status}
                          </div>
                        </div>
                      ) : (
                        <span className="text-gray-400 text-sm">No active order</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {box.current_zone ? (
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          box.current_zone === 'VEHICLE'
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-purple-100 text-purple-800'
                        }`}>
                          {box.current_zone}
                        </span>
                      ) : (
                        <span className="text-gray-400 text-sm">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <span className="px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-xs font-medium">
                        {box.status}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      {box.last_used_at ? (
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Clock size={14} />
                          {new Date(box.last_used_at).toLocaleDateString()}
                        </div>
                      ) : (
                        <span className="text-gray-400 text-sm">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default BoxReceivingDashboard;
